'use strict';

/* global angular */

const _ = require('lodash');
const moment = require('moment');

/** @MeasurementStore */
const Store = function(ResourceHelper, Flux, APP_DISPATCHER_ID, MapActions, AnalyticsService) {

  const SiteStore = Flux.Store.get('SiteStore');

  const store = this;

  // Intialize the store
  const MeasurementStore = Flux.Store.create({
    name: 'MeasurementStore',
    events: {
      Changed: true,
      ActiveSaved: true,
      RequestCompleted: true,
      RequestFailed: true,
      MeasurementUpdated: true,
      MeasurementDestroyed: true,
      NameUpdated: true,
      LayerAdded: true, // Dispatch when polygon is created,
      DataCaptureChanged: true // Dispatched when a Datacapture change is detected while editing
    }
  });

  /**
   * Initial empty store state
   * @var {Object} $state
   */
  MeasurementStore.$state = {
    current : null, // The measurements displayed on screen (TBD: Only one at a time?)
    available : null,
    active: null // Measurement currently being edited
  };

  /**
   * Intialize the store bindings and initial data.
   * @constructor
   */
  MeasurementStore.init = function () {
    var d = Flux.Dispatcher.get(APP_DISPATCHER_ID);

    d.register(MapActions.NewMeasurementStart, this._handleNewMeasurementStart.bind(this));
    d.register(MapActions.NewMeasurementRequest, this._handleNewMeasurementRequest.bind(this));
    d.register(MapActions.SaveActiveMeasurement, this._handleSaveActiveMeasurement.bind(this));
    d.register(MapActions.MeasurementEditEnd, this._handleMeasurementEditEnd.bind(this));
    d.register(MapActions.EditMeasurementStart, this._handleEditMeasurementStart.bind(this));
    d.register(MapActions.DestroyMeasurement, this._handleDestroyMeasurement.bind(this));
  };

  const getDefaultMeasurementName = () => {
    return 'Volume ' + moment().format('MM/DD/YYYY HH:mm:ss');
  };

  MeasurementStore._handleNewMeasurementStart = () => {
    var Measurement = ResourceHelper.getResource('Measurement');
    var SiteStore = Flux.Store.get('SiteStore');
    var UserStore = Flux.Store.get('UserStore');
    var DataCaptureStore = Flux.Store.get('DataCaptureStore');

    // TODO: FIXME: Determine if we need to create a new category
    // category if doesn't exist, if it can be optional or we'll have
    // always a default one;
    var newMeasurement = Measurement.createInstance({
      siteId: SiteStore.get('current').id,
      userId: UserStore.get('user').id,
      dataCaptureId: DataCaptureStore.get('current').id,
      type: 'volume',
      id: '_tmp_measurement' + Date.now() + '_' + Math.floor(Math.random() * 10000),
      layers: [],
      name: getDefaultMeasurementName()
    });
    MeasurementStore.setState({
      active : newMeasurement
    });
  };

  MeasurementStore._activeMeasurementBeforeEdit = null;

  MeasurementStore._revertChanges = () => {
    var previous = MeasurementStore._activeMeasurementBeforeEdit;
    var Measurement = ResourceHelper.getResource('Measurement');

    if (!previous) {
      return;
    }

    delete previous.site;
    Measurement.inject(previous);
    MeasurementStore._activeMeasurementBeforeEdit = null;
  };

  MeasurementStore._handleEditMeasurementStart = (ev) => {
    var Measurement = ResourceHelper.getResource('Measurement');
    var measurement = Measurement.get(ev.measurementId);
    MeasurementStore._activeMeasurementBeforeEdit = _.cloneDeep(measurement);
    MeasurementStore.setState({
      active : measurement
    });
  };

  MeasurementStore.isNewRecord = (measurement) => {
    var exists = measurement && measurement.id && !/^_tmp/.test(measurement.id);
    return !exists;
  };

  var _wrapInFeatureCollection = (layer) => {
    return {
      type: 'FeatureCollection',
      features: [layer.data]
    };
  };

  MeasurementStore.getPermissions = (measurementId) => {
    var measurement = _.find(MeasurementStore.get('available'), 'id', measurementId);
    if (!measurement) {
      return {};
    }
    var permissions = measurement._permissions || {};
    var UserStore = Flux.Store.get('UserStore');
    var userId = UserStore.get('user').id;
    return {
      read: permissions.read || permissions.all,
      edit: permissions.edit || permissions.all,
      // TODO: Check why permissionsUtil is not returning `delete` to own resources
      delete: permissions.delete || permissions.all || measurement.userId === userId
    };
  };

  /*
   * This method is for formatting the measurement *back* to the model format
   * so it can be saved
   */
  var _formatMeasurement = (measurement) => {
    var formatted = _.cloneDeep(measurement);
    formatted.name = formatted.name || getDefaultMeasurementName();
    formatted.request = _wrapInFeatureCollection(measurement.layers[0]);

    // Since the layer attributes get added to the object which can be the
    // request or the result, we need to make sure the values are stripped
    // down from the request before sending them back
    formatted.request.features.forEach((feature) => {
      delete feature.properties.fillVolume;
      delete feature.properties.cutVolume;
      delete feature.properties.totalVolume;
      delete feature.properties.height;
    });

    // TODO: BUGFIX
    // Editing the geometry sometimes results on the coordinates being saved as
    // strings instead of numbers. Leaflet handles this well but the geojson
    // validation in the backend breaks. Fix the model here before sending.
    [formatted.request, formatted.result].forEach((field) => {
      if (field) {
        field.features.forEach((feature) => {
          feature.geometry.coordinates = feature.geometry.coordinates.map((coords) => {
            return _.map(coords, (coord) => {
              return _.map(coord, (latLng) => {
                return parseFloat(latLng);
              });
            });
          });
        });
      }
    });

    delete formatted.id;
    delete formatted.site;
    delete formatted._permissions;
    delete formatted.userName;
    delete formatted.layers;
    return formatted;
  };

  /*
   * This method transforms the measurement *from* the original model, it stores
   * by reference the layers so they can be edited directly by the map booster.
   * Since the measurement doesn't have multiple layers, but rather stores a
   * GeoJSON with potentially multiple geometry objects (features), we create
   * the virtual layers attribute. We modify the object directly so it is passed
   * by reference
   */
  var _parseMeasurement = (measurement) => {
    var featureCollection = measurement.result || measurement.request || {
      features: [] // Case for newly created measurement that doesn't have layers
    };
    if (measurement.result && measurement.request) {
      measurement.request.features.forEach((feature, index) => {
        if (feature.properties) {
          measurement.result.features[index].properties.style = feature.properties.style;
        }
      });
    }
    measurement.layers = _.map(featureCollection.features, (feature, featureIdx) => {
      return {
        resource: 'measurement',
        type: 'polygon',
        data: feature,
        id: 'measurement-' + measurement.id + '-layer-' + featureIdx
      };
    });
    return measurement;
  };

  var syncSubscribe = (measurement) => {
    var SyncStore = Flux.Store.get('SyncStore');
    SyncStore.subscribe(measurement.subKey, measurement.subChannel, {
      message: (msg) => {
        if (msg.status === 'complete') {
          MeasurementStore.emit(MeasurementStore.Events.RequestCompleted, measurement.id);
        }
        else if (msg.status === 'error') {
          MeasurementStore.emit(MeasurementStore.Events.RequestFailed, measurement.id);
        }
        else if (msg.status === 'started') {
          MeasurementStore.emit(MeasurementStore.Events.RequestCompleted, measurement.id);
        }
      }
    });
  };

  var filterMeasurement = (measurement) => {
    return ['completed', 'new', 'processing', 'error'].indexOf(measurement.status) !== -1 &&
      measurement.isDeleted !== 1;
  };

  MeasurementStore._handleNewMeasurementRequest = () => {
    var Measurement = ResourceHelper.getResource('Measurement');
    var activeMeasurement = MeasurementStore.get('active');
    var site = SiteStore.get('current');

    if (activeMeasurement) {
      var measurementRecord = _formatMeasurement(activeMeasurement);
      Measurement.create(measurementRecord)
      .then((measurement) => {
        measurement = _parseMeasurement(measurement);
        syncSubscribe(measurement);
        AnalyticsService.track('[CORE] Measurements', 'New Measurement', {
          measurementId: measurement.id,
          siteId: site.id,
          siteName: site.name
        });
        MeasurementStore.emit(MeasurementStore.Events.ActiveSaved);
        store.updateMeasurementData(measurement);
      });
    }
    else {
      console.warn('No active measurement to save');
    }
  };

  MeasurementStore._handleSaveActiveMeasurement = () => {
    var reprocess = false;
    var params = {};

    var Measurement = ResourceHelper.getResource('Measurement');
    var activeMeasurement = MeasurementStore.get('active');
    var site = SiteStore.get('current');

    var measurementToSave = _formatMeasurement(activeMeasurement);
    var previous = MeasurementStore._activeMeasurementBeforeEdit;

    /*
      This flag will determine if we need to recalculate the measurement.
      We are diffing the geometry of the new and old layers, but depending of who updated the layer last,
      the coordinates can be numbers or strings, so we are applyng `parseFloat` on both.
    */
    reprocess = (() => {
      const getNormalizedCoords = (layer) => {
        return  _.chain(layer).get(['layers', 0, 'data', 'geometry', 'coordinates', 0])
        .flattenDeep()
        .map((el) => {
          return parseFloat(el);
        }).value();
      };

      return !_.isEqual(getNormalizedCoords(previous), getNormalizedCoords(activeMeasurement));
    })();

    if (reprocess) {
      measurementToSave.status = 'processing';
      measurementToSave.result = null;
      params.reprocess = true;
    }

    if (activeMeasurement) {
      Measurement.update(activeMeasurement.id, _.omit(measurementToSave, 'createdAt'), {
        params: params
      })
      .then((measurement) => {
        syncSubscribe(measurement);
        AnalyticsService.track('[CORE] Measurements', 'Edit Measurement', {
          measurementId: measurement.id,
          siteId: site.id,
          siteName: site.name
        });
        MeasurementStore.emit(MeasurementStore.Events.ActiveSaved);
        store.updateMeasurementData(measurement);
      });
      // Clear cache for previous version
      MeasurementStore._activeMeasurementBeforeEdit = null;
    }
    else {
      console.warn('No active measurement to update');
    }
  };

  MeasurementStore.addLayer = (layer) => {
    var active = MeasurementStore.$state.active;
    if (!active) {
      console.warn('No active measurement to update');
    }

    // We don't support layers for annotations yet. When we do, the geojson will
    // constain a single polygon which we will have to merge into the same geojson
    // feature with multiple polygons.
    // Since we support single layer, overwrite it with the wrapped geoJSON
    active.layers = [{
      resource: 'measurement',
      type: 'polygon',
      data: layer,
      id: 'measurement-' + active.id + '-layer-' + Math.floor(Math.random() * 1000)
    }];

    MeasurementStore.setState({
      active: active
    });
    MeasurementStore.emit(MeasurementStore.Events.LayerAdded);
  };

  MeasurementStore._handleMeasurementEditEnd = (ev) => {
    var save = ev.saveChanges;
    if (save !== undefined && !save) {
      MeasurementStore._revertChanges();
    }
    MeasurementStore.setState({
      active: null
    });
  };

  MeasurementStore._handleDestroyMeasurement = (ev) => {
    var Measurement = ResourceHelper.getResource('Measurement');
    var measurement = _.find(MeasurementStore.get('available'), 'id', ev.measurementId);
    var currentSite = SiteStore.get('current');
    if (!measurement) {
      return;
    }
    Measurement.destroy(measurement)
    .then(() => {
      Measurement.eject(measurement);
      MeasurementStore.setState({
        available: currentSite.measurements.filter(filterMeasurement),
        current: _.reject(MeasurementStore.$state.current, 'id', measurement.id)
      });
      AnalyticsService.track('[CORE] Measurements', 'Delete measurement', {
        measurementId: measurement.id,
        siteId: currentSite.id,
        siteName: currentSite.name
      });
      MeasurementStore.emit(MeasurementStore.Events.MeasurementDestroyed);
    });
  };

  MeasurementStore.changeActiveName = (name) => {
    var active = MeasurementStore.$state.active;
    if (!active) {
      console.warn('No active measurement to update');
    }
    active.name = name;
    MeasurementStore.emit(MeasurementStore.Events.NameUpdated);
  };

  /**
   * Sets the internal state for the Store.
   * @param {state} object An MeasurementStore state object.
   */
  MeasurementStore.setState = (state) => {
    angular.extend(MeasurementStore.$state, state);
    MeasurementStore.emit(MeasurementStore.Events.Changed, MeasurementStore.$state);
  };

  /**
   * Activates the measurement on screen
   */
  MeasurementStore.activate = (id) => {
    var available = MeasurementStore.get('available');
    var current = MeasurementStore.get('current');
    var site = SiteStore.get('current');

    if (!current) {
      // Should not enter here, current should be at least an empty array
      return;
    }
    // Do nothing if it's already active
    if (!_.find(current, 'id', id)) {
      var element = _.find(available, 'id', id);
      // Requested a measurement that was not available
      if (!element) {
        console.error('Measurement requested not found: ', id);
      }
      else {
        AnalyticsService.track('[CORE] Measurements', 'Edit Measurement', {
          measurementId: element.id,
          siteId: site.id,
          siteName: site.name
        });
        // Set a temp id, since layers on measurements are not saved separately
        if (!element.request.id) {
          element.request.id = '_tmp_measurement_layer-' + Date.now() + '_' + Math.floor(Math.random() * 10000);
        }
        MeasurementStore.setState({
          // Force a new array to trigger changes
          current : current.concat([element]).filter((measurement) => {
            return measurement.dataCaptureId === element.dataCaptureId;
          })
        });
      }
    }
  };

  /**
   * Removes the Measurement with id `id` to the list of `current` Measurements
   * if present
   */
  MeasurementStore.deactivate = (id) => {
    var current = MeasurementStore.get('current');
    if (!current) {
      return;
    }
    var element = _.find(current, 'id', id);
    if (element) {
      MeasurementStore.setState({
        current: _.reject(current, 'id', id)
      });
    }
  };

  // Used when the datacapture changes, deactivate all measurements on screen
  // because the data is different
  MeasurementStore.deactivateAll = () => {
    MeasurementStore.setState({
      current: []
    });
  };

  //////////////////////////////////////////////////////////////////////////////
  // Event Handlers
  //////////////////////////////////////////////////////////////////////////////

  this.syncSubscribeMeasurements = () => {
    _.forEach(MeasurementStore.$state.available, (measurement) => {
      if ((measurement.status === 'new' || measurement.status === 'processing') && measurement.subKey && measurement.subChannel) {
        syncSubscribe(measurement);
      }
    });
  };

  this.updateMeasurementData = (measurement) => {
    var currentSite = SiteStore.get('current');
    var current = MeasurementStore.$state.current;
    var available = _.chain(currentSite.measurements).map(_parseMeasurement).filter(filterMeasurement).value();
    var newActive;
    if (!_.find(current, 'id', measurement.id)) {
      newActive = _.find(available, 'id', measurement.id);
      if (newActive) {
        current.push(newActive);
      }
    }
    MeasurementStore.setState({
      available: available,
      current: current
    });
  };

  // This method responds to a site change and resets the $state variables to
  // reflect the new resources
  this._handleSiteChange = () => {
    var currentSite = SiteStore.get('current');
    if (currentSite) {
      MeasurementStore.setState({
        available: _.chain(currentSite.measurements).map(_parseMeasurement).filter(filterMeasurement).value(),
        current: [],
        active: null
      });
      this.syncSubscribeMeasurements();
    }
  };

  this._handleRequestCompleted = (id) => {
    var Measurement = ResourceHelper.getResource('Measurement');
    Measurement.find(id, {
      bypassCache: true,
      params: {
        include: 'userName'
      }
    })
    .then((measurement) => {
      measurement = _parseMeasurement(measurement);
      MeasurementStore.emit(MeasurementStore.Events.MeasurementUpdated, measurement);

      var available = MeasurementStore.$state.available;
      _.remove(available, 'id', measurement.id);
      MeasurementStore.setState({
        available: available.concat([measurement]) // Return another object to force an update
      });
    });
  };

  /*
   * This event is triggered when the datacapture is changed while having a measurement edit tool active
   */
  this._handleDataCaptureChanged = () => {
    var active = MeasurementStore.$state.active;
    if (!active) {
      return;
    }

    var DataCaptureStore = Flux.Store.get('DataCaptureStore');
    var currentDataCapture = DataCaptureStore.get('current');
    active.dataCaptureId = currentDataCapture.id;
  };

  SiteStore.on(SiteStore.Events.Changed, this._handleSiteChange.bind(this));
  MeasurementStore.on(MeasurementStore.Events.RequestCompleted, this._handleRequestCompleted.bind(this));
  MeasurementStore.on(MeasurementStore.Events.RequestFailed, this._handleRequestCompleted.bind(this));
  MeasurementStore.on(MeasurementStore.Events.DataCaptureChanged, this._handleDataCaptureChanged.bind(this));

  return MeasurementStore;

};

Store.$inject = ['ResourceHelper', 'sbFlux', 'APP_DISPATCHER_ID', 'MapActions', 'AnalyticsService'];
module.exports = Store;



// WEBPACK FOOTER //
// ./src/app/dash/dataStores/MeasurementStore.js